import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { LinkDialog } from "@/components/LinkDialog";
import { DatePicker } from "@/components/pickers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Link2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function JoinLinkPage() {
  const { actingOrgId } = useAuth();
  const qc = useQueryClient();
  const [expires, setExpires] = useState("");
  const [link, setLink] = useState("");

  const { data } = useQuery({
    queryKey: ["join-link", actingOrgId],
    queryFn: async () => (await api.get("/org/join-link")).data,
  });

  const enable = useMutation({
    mutationFn: async () => (await api.post("/org/join-link/enable", { expires_at: expires || null })).data,
    onSuccess: (d) => {
      toast.success("Join link enabled");
      if (d.link) setLink(d.link);
      qc.invalidateQueries({ queryKey: ["join-link"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const regen = useMutation({
    mutationFn: async () => (await api.post("/org/join-link/regenerate", { expires_at: expires || null })).data,
    onSuccess: (d) => {
      toast.success("New link generated — the old one no longer works");
      if (d.link) setLink(d.link);
      qc.invalidateQueries({ queryKey: ["join-link"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const disableM = useMutation({
    mutationFn: async () => (await api.post("/org/join-link/disable")).data,
    onSuccess: () => { toast.success("Join link disabled"); qc.invalidateQueries({ queryKey: ["join-link"] }); },
  });

  return (
    <div>
      <PageHeader title="Join link" description="A fallback way for employees to sign up when invite emails don't arrive" />
      <Card className="max-w-xl space-y-5 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600"><Link2 className="h-5 w-5" /></div>
            <div>
              <p className="font-medium text-slate-800">Join link</p>
              <p className="text-xs text-slate-400">{data?.enabled ? "Enabled" : "Disabled"}</p>
            </div>
          </div>
          <Switch checked={!!data?.enabled} data-testid="join-link-toggle"
            onCheckedChange={(v) => v ? enable.mutate() : disableM.mutate()} />
        </div>

        <div>
          <Label className="text-xs">Optional expiry date</Label>
          <div className="mt-1.5 max-w-[220px]"><DatePicker mode="iso" min={new Date().toISOString().slice(0, 10)} value={expires} onChange={setExpires} clearable placeholder="No expiry" testid="join-link-expiry" /></div>
          {data?.expires_at && <p className="mt-1 text-xs text-slate-400">Current expiry: {data.expires_at}</p>}
        </div>

        <div className="flex gap-2">
          <Button variant="outline" disabled={!data?.enabled} onClick={() => regen.mutate()} data-testid="join-link-regenerate">
            <RefreshCw className="mr-2 h-4 w-4" /> Regenerate link
          </Button>
          <Button variant="outline" disabled={!data?.enabled || !data?.has_link} onClick={() => enable.mutate()} data-testid="join-link-copy">
            Show link
          </Button>
        </div>
        <p className="text-xs text-slate-400">Regenerating creates a fresh link and immediately stops the previous one from working.</p>
      </Card>

      <LinkDialog open={!!link} onClose={() => setLink("")} title="Organisation join link"
        note="Share this link with employees. Anyone with it can sign up to your organisation." link={link} />
    </div>
  );
}
